import {
  createStyles,
  makeStyles,
  Table,
  TableCell,
  TableContainer,
  TableRow,
  TableBody,
  CircularProgress,
  Divider,
  useMediaQuery,
  Theme,
  Checkbox,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { useState, useEffect, useImperativeHandle, forwardRef, ForwardRefRenderFunction } from 'react';
import { PayerTransaction, PayerTransactionConnection, OrderDirection, CurrencyType, MerchantInfo } from '../gql-types.generated';
import InfiniteScroll from 'react-infinite-scroller';
import TableHeader, { HeaderCell } from './TableHeader';
import { PaymentsDueRowData } from '../custom-types/PaymentsDueRowData';
import PaymentsDueRow from './PaymentsDueRow';
import PaymentsDueFooter from './PaymentsDueFooter';
import { DateTime } from 'luxon';

const useStylesMobile = makeStyles(() =>
  createStyles({
    tableWrapContainer: {
      display: 'flex',
      flexFlow: 'column',
      height: '70vh',
    },
    table: {
      overflow: 'auto',
      margin: '0px',
    },
    tableWrap: {
      flex: '1 1 auto',
      overflow: 'auto',
    },
    tableFooter: {
      flex: '0 1 auto',
    },
    firstTableCell: {
      padding: '5px 10px 5px 16px',
    },
    cellData: {
      display: 'flex',
      padding: '2px',
    },
    cellDetails: {
      width: '75%',
      fontSize: '16px',
    },
    cellDate: {
      fontSize: '14px',
      margin: '2px 1px 0px auto',
      width: '25%',
      textAlign: 'right',
    },
    cellReferenceNumber: {
      marginTop: '2px',
      color: '#276EF1',
      wordBreak: 'break-word',
    },
    cellAmount: {
      color: '#000000DE',
    },
    failedCellAmount: {
      color: '#FF3B30',
    },
    emptyMessage: {
      fontSize: '14px',
      textAlign: 'center',
      fontStyle: 'oblique',
    },
    loadingSpinner: {
      height: 60,
      width: 60,
      display: 'inline-block',
    },
    errorIcon: {
      fontSize: '20px',
      verticalAlign: 'text-bottom',
    },
    errorMessage: {
      fontSize: '18px',
      textAlign: 'center',
      fontStyle: 'oblique',
      color: 'red',
    },
    bottomSummary: {
      padding: 4,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    invoiceText: {
      marginLeft: 4,
      fontWeight: 200,
    },
    totalDueText: {
      marginLeft: 8,
    },
  }),
);

const useStylesDesktop = makeStyles(() =>
  createStyles({
    table: {
      overflow: 'auto',
      margin: '0px',
    },
    tableWrap: {
      maxHeight: '55vh',
      overflow: 'auto',
    },
    tableCell: {
      fontSize: 16,
      wordWrap: 'break-word',
      padding: '16px 4px',
    },
    emptyMessage: {
      fontSize: '14px',
      textAlign: 'center',
      fontStyle: 'oblique',
    },
    loadingSpinner: {
      height: 60,
      width: 60,
      display: 'inline-block',
    },
    errorIcon: {
      fontSize: '20px',
      verticalAlign: 'text-bottom',
    },
    failedCell: {
      fontSize: 16,
      wordWrap: 'break-word',
      color: '#FF3B30',
    },
    errorMessage: {
      fontSize: '18px',
      textAlign: 'center',
      fontStyle: 'oblique',
      color: 'red',
    },
    referenceCell: {
      fontSize: 16,
      color: '#276EF1',
      wordWrap: 'break-word',
      wordBreak: 'break-word',
      paddingLeft: '35px',
    },
    bottomSummary: {
      padding: '32px 16px',
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
    invoiceText: {
      marginLeft: 8,
      fontWeight: 200,
    },
    totalDueText: {
      marginLeft: 24,
    },
    payButton: {
      marginLeft: 64,
      width: 140,
    },
  }),
);

export interface PaymentsDueListHandles {
  consolidatedOrPartialOnPay: (index: number) => void;
}

interface PaymentsDueListProps {
  platform: string;
  paymentsDue: PayerTransaction[] | undefined;
  paymentsDueConnection: PayerTransactionConnection | undefined;
  loadPage: () => void;
  onClickPay: (urlToken: string | null | undefined) => void;
  showPaymentDetails: (paymentDetails: PayerTransaction | null | undefined, detailsIndex?: number, isSelected?: boolean) => void;
  paymentsDueError: Error | undefined;
  merchantName: string | undefined;
  settingsMenuVisible: boolean;
  paymentsDueDataSort: (
    paymentsDue: PaymentsDueRowData[] | undefined,
    paymentsDueOrder: OrderDirection,
    paymentsDueOrderBy: string,
  ) => PaymentsDueRowData[] | undefined;
  onClickPayConsolidatedOrPartial: (selectedData: PaymentsDueRowData[]) => void;
  paymentsDueData: PaymentsDueRowData[] | undefined;
  fetchPaymentsDueData: (payments: PaymentsDueRowData[] | undefined) => void;
  consolidatedOrPartialCancelClicked: boolean;
  selectedMerchantInfo: MerchantInfo | undefined;
  switchedToRecentPayments: boolean;
  amountError: number;
  setAmountError: (amountError: number) => void;
  clearPaymentRequestInfoList: () => void;
}

const PaymentsDueList: ForwardRefRenderFunction<PaymentsDueListHandles, PaymentsDueListProps> = (props, ref) => {
  const {
    platform,
    paymentsDue,
    paymentsDueConnection,
    loadPage,
    onClickPay,
    showPaymentDetails,
    paymentsDueError,
    settingsMenuVisible,
    paymentsDueDataSort,
    onClickPayConsolidatedOrPartial,
    paymentsDueData,
    fetchPaymentsDueData,
    consolidatedOrPartialCancelClicked,
    selectedMerchantInfo,
    switchedToRecentPayments,
    amountError,
    setAmountError,
    clearPaymentRequestInfoList,
  } = props;
  const classesMobile = useStylesMobile();
  const classesDesktop = useStylesDesktop();
  const [paymentsDueOrder, setPaymentsDueOrder] = useState<OrderDirection>(
    localStorage.getItem('paymentsDueDataOrder') === 'asc' ? OrderDirection.Asc : OrderDirection.Desc,
  );
  const [paymentsDueOrderBy, setPaymentsDueOrderBy] = useState<string>(
    localStorage.getItem('paymentsDueDataOrderBy') || 'receivedDate',
  );
  const [selectedInvoices, setSelectedInvoices] = useState<number>(0);
  const [selectedInvoicesAmount, setSelectedInvoicesAmount] = useState<number>(0);
  const [hideUnselected, setHideUnselected] = useState<boolean>(false);

  const emptyMessage = 'No payments due.';
  const hasMoreData = paymentsDueConnection?.pageInfo.hasNextPage as boolean;
  const errorMessage = 'Unable to load data. Please try again later.';
  const matches = useMediaQuery((theme: Theme) => theme.breakpoints.down(1400));
  const consolidatedAllowed = !!selectedMerchantInfo?.features?.paymentRequests.consolidatedPayment;
  const partialAllowed = !!selectedMerchantInfo?.features?.paymentRequests.partialPayment;
  const hasDiscounts = !!paymentsDue?.find(paymentDue => {
    return !!paymentDue.paymentRequest?.discountEndDate && !!paymentDue.paymentRequest.discountAmount;
  });
  const creditAllowed = !!selectedMerchantInfo?.features?.creditMemos?.enabled;

  useEffect(() => {
    if (!(consolidatedOrPartialCancelClicked || switchedToRecentPayments)) {
      const paymentRequests: PaymentsDueRowData[] = [];
      if (paymentsDue) {
        paymentsDue.forEach(paymentDue => {
          //calculating discount amount
          const discountEndDate = DateTime.fromISO(paymentDue.paymentRequest?.discountEndDate);
          const discountEndValid = discountEndDate.isValid && discountEndDate > DateTime.utc();
          const discountAmount = (discountEndValid && paymentDue.paymentRequest?.discountAmount) || 0;

          //calculating credit amount
          let creditAmount = 0;
          paymentDue?.paymentRequest?.payments?.forEach(paymentData => {
            creditAmount += paymentData?.creditAmount || 0;
          });

          paymentRequests.push({
            isSelected: false,
            paymentDue,
            amount: (paymentDue.paymentRequest?.totalDue || 0) - discountAmount,
            discountAmount,
            creditAmount,
          });
        });
        const localOrder = localStorage.getItem('paymentsDueDataOrder') === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
        const localOrderBy = localStorage.getItem('paymentsDueDataOrderBy');
        if (localOrder && localOrderBy) {
          fetchPaymentsDueData(paymentsDueDataSort(paymentRequests, localOrder, localOrderBy));
        } else {
          fetchPaymentsDueData(paymentRequests);
        }
      }

      if (selectedInvoices !== 0) setSelectedInvoices(0);
      if (selectedInvoicesAmount !== 0) setSelectedInvoicesAmount(0);
      if (hideUnselected) setHideUnselected(false);
      if (amountError !== 0) setAmountError(0);
    } else {
      let invoicesSelected = 0;
      let amountSelected = 0;
      paymentsDueData?.forEach(item => {
        if (item.isSelected) {
          invoicesSelected += 1;
          amountSelected += item.amount || 0;
        }
      });
      setSelectedInvoices(invoicesSelected);
      setSelectedInvoicesAmount(amountSelected);

      if (invoicesSelected > 0 && !consolidatedAllowed && partialAllowed) {
        setHideUnselected(true);
      }

      if (invoicesSelected === 0) {
        setHideUnselected(false);
      }
    }
    clearPaymentRequestInfoList();
  }, [paymentsDue]);

  const sortPaymentsDueData = (orderBy: string) => {
    const isDesc = orderBy === paymentsDueOrderBy && paymentsDueOrder === OrderDirection.Desc;
    const order = isDesc ? OrderDirection.Asc : OrderDirection.Desc;
    setPaymentsDueOrder(order);
    setPaymentsDueOrderBy(orderBy);
    fetchPaymentsDueData(paymentsDueDataSort(paymentsDueData, order, orderBy));
    localStorage.setItem('paymentsDueDataOrder', isDesc ? 'asc' : 'desc');
    localStorage.setItem('paymentsDueDataOrderBy', orderBy);
  };

  const getTotalAmountDue = () => {
    let totalAmount = 0;
    if (paymentsDueData) {
      paymentsDueData.forEach(row => {
        totalAmount += row.amount;
      });
    }
    return totalAmount;
  };

  const handleRowClicked = (rowIndex: number) => {
    if (paymentsDueData) {
      const paymentsDueitems = [...paymentsDueData];
      const paymentsDueitem = { ...paymentsDueitems[rowIndex] };
      paymentsDueitem.isSelected = !paymentsDueitem.isSelected;
      const discountEndDate = DateTime.fromISO(paymentsDueitem.paymentDue.paymentRequest?.discountEndDate);
      const discountEndValid = discountEndDate.isValid && discountEndDate > DateTime.utc();
      const discountAmount = (discountEndValid && paymentsDueitem.paymentDue.paymentRequest?.discountAmount) || 0;
      const totalDue = paymentsDueitem.paymentDue.paymentRequest?.totalDue;
      paymentsDueitem.amount = totalDue ? totalDue - discountAmount : 0;
      paymentsDueitems[rowIndex] = paymentsDueitem;
      fetchPaymentsDueData(paymentsDueitems);

      const invoices = paymentsDueitem.isSelected ? selectedInvoices + 1 : selectedInvoices - 1;
      const invoicesAmount = paymentsDueitem.isSelected
        ? selectedInvoicesAmount + (paymentsDueitem?.amount || 0)
        : selectedInvoicesAmount - (paymentsDueitem?.amount || 0);

      setSelectedInvoices(invoices);
      setSelectedInvoicesAmount(invoicesAmount);

      if (!consolidatedAllowed && partialAllowed) {
        setHideUnselected(!hideUnselected);
      }

      if (invoices === 0) {
        setHideUnselected(false);
      }
    }
  };

  useImperativeHandle(ref, () => ({
    consolidatedOrPartialOnPay: (index: number) => {
      handleRowClicked(index);
    },
  }));

  const handlePayment = (payAll: boolean) => {
    let paymentsDueSubmitData = paymentsDueData || [];
    if (!payAll) {
      paymentsDueSubmitData = [];
      paymentsDueData?.forEach(item => {
        if (item.isSelected) {
          paymentsDueSubmitData.push(item);
        }
      });
    }
    onClickPayConsolidatedOrPartial(paymentsDueSubmitData);
  };

  const handleAmountChange = (rowIndex: number, amount: number) => {
    if (paymentsDueData) {
      const paymentsDueitems = [...paymentsDueData];
      const paymentsDueitem = { ...paymentsDueitems[rowIndex] };
      paymentsDueitem.amount = amount;
      paymentsDueitems[rowIndex] = paymentsDueitem;
      fetchPaymentsDueData(paymentsDueitems);

      //need to handle amount in total
    }
  };

  const handleAmountError = (hasError: boolean) => {
    const error = hasError ? amountError + 1 : amountError - 1;
    setAmountError(error);
  };

  const getTotalPartialAmount = () => {
    if (paymentsDueData) {
      let totalAmount = 0;
      paymentsDueData.forEach(row => {
        if (row.isSelected) {
          totalAmount += row.amount;
        }
      });
      return totalAmount;
    }
    return 0;
  };

  const skeletonMobile: JSX.Element[] = [];
  for (let i = 0; i < 7; i += 1) {
    skeletonMobile.push(
      <TableRow>
        <TableCell>
          <div style={{ display: 'flex' }}>
            <div style={{ width: '80%' }}>
              <Skeleton height="1.5em" width="8em" />
              <Skeleton height="1.5em" width="6em" />
              <Skeleton height="1.5em" width="4em" />
            </div>
            <div style={{ width: '20%', textAlign: 'right' }}>
              <Skeleton height="1.5em" width="5em" />
              <Skeleton height="1.5em" width="5em" />
              <Skeleton height="1.5em" width="5em" />
            </div>
          </div>
        </TableCell>
      </TableRow>,
    );
  }

  const skeletonDesktop: JSX.Element[] = [];
  for (let i = 0; i < 7; i += 1) {
    skeletonDesktop.push(
      <TableRow key={i}>
        <TableCell align="right" className={classesDesktop.tableCell}>
          <Checkbox disabled />
        </TableCell>
        <TableCell className={classesDesktop.tableCell}>
          <Skeleton height="1.5em" width="80%" />
        </TableCell>
        <TableCell className={classesDesktop.tableCell}>
          <Skeleton height="1.5em" width="80%" />
        </TableCell>
        <TableCell className={classesDesktop.tableCell}>
          <Skeleton height="1.5em" width="80%" />
        </TableCell>
        <TableCell className={classesDesktop.tableCell}>
          <Skeleton height="1.5em" width="80%" />
        </TableCell>
        <TableCell align="right" className={classesDesktop.tableCell}>
          <Skeleton height="1.5em" width="50%" style={{ marginLeft: 'auto' }} />
        </TableCell>
        {partialAllowed && creditAllowed && (
          <TableCell className={classesDesktop.tableCell}>
            <Skeleton height="1.5em" width="50%" style={{ marginLeft: 'auto' }} />
          </TableCell>
        )}
        {partialAllowed && (
          <TableCell className={classesDesktop.tableCell}>
            <Skeleton height="1.5em" width="50%" style={{ marginLeft: 'auto' }} />
          </TableCell>
        )}
        {partialAllowed && (
          <TableCell className={classesDesktop.tableCell}>
            <Skeleton height="1.5em" width="50%" style={{ marginLeft: 'auto' }} />
          </TableCell>
        )}
        <TableCell />
      </TableRow>,
    );
  }

  const desktopHeaderCells: HeaderCell[] = [
    { id: 'checkbox', label: '' },
    { id: 'reference', label: 'Reference #', sortable: true },
    { id: 'dueDate', label: 'Due Date', sortable: true },
    { id: 'receivedDate', label: 'Received On', sortable: true },
    { id: 'status', label: 'Status', sortable: true },
    { id: 'amount', label: 'Total', sortable: true, align: 'right' },
    { id: 'actions', label: '' },
  ];

  const desktopPartialHeaderCells: HeaderCell[] = [
    { id: 'checkbox', label: '' },
    { id: 'reference', label: 'Reference #', sortable: true },
    { id: 'dueDate', label: 'Due Date', sortable: true },
    { id: 'receivedDate', label: 'Received On', sortable: true },
    { id: 'status', label: 'Status', sortable: true },
    { id: 'amount', label: 'Total', sortable: true, align: 'right' },
    { id: 'paid', label: 'Paid', sortable: true, align: 'right' },
    { id: 'balance', label: 'Balance', sortable: true, align: 'right' },
    { id: 'actions', label: '' },
  ];

  const desktopCreditHeaderCells: HeaderCell[] = [
    { id: 'checkbox', label: '' },
    { id: 'reference', label: 'Reference #', sortable: true },
    { id: 'dueDate', label: 'Due Date', sortable: true },
    { id: 'receivedDate', label: 'Received On', sortable: true },
    { id: 'status', label: 'Status', sortable: true },
    { id: 'amount', label: 'Total', sortable: true, align: 'right' },
    { id: 'credit', label: 'Credit Applied', sortable: true, align: 'right' },
    { id: 'paid', label: 'Paid', sortable: true, align: 'right' },
    { id: 'balance', label: 'Balance', sortable: true, align: 'right' },
    { id: 'actions', label: '' },
  ];

  if (hasDiscounts) {
    const discountEnd = { id: 'discountEnd', label: 'Discount End', sortable: true };
    desktopHeaderCells.splice(2, 0, discountEnd);
    desktopPartialHeaderCells.splice(2, 0, discountEnd);
    desktopCreditHeaderCells.splice(2, 0, discountEnd);
  }

  const partialHeaderCells = creditAllowed ? desktopCreditHeaderCells : desktopPartialHeaderCells;

  const getColumns = () => {
    if (partialAllowed && hasDiscounts && creditAllowed) {
      return (
        <>
          <col width="3%" />
          <col width="16%" />
          <col width="8%" />
          <col width="8%" />
          <col width="8%" />
          <col width="8%" />
          <col width="8%" />
          <col width="9%" />
          <col width="7%" />
          <col width="7%" />
          <col width="18%" />
        </>
      );
    }
    if (partialAllowed && creditAllowed) {
      return (
        <>
          <col width="3%" />
          <col width="17%" />
          <col width="10%" />
          <col width="8%" />
          <col width="8%" />
          <col width="10%" />
          <col width="10%" />
          <col width="7%" />
          <col width="7%" />
          <col width="20%" />
        </>
      );
    }
    if (partialAllowed && hasDiscounts) {
      return (
        <>
          <col width="3%" />
          <col width="17%" />
          <col width="10%" />
          <col width="10%" />
          <col width="8%" />
          <col width="8%" />
          <col width="10%" />
          <col width="7%" />
          <col width="7%" />
          <col width="20%" />
        </>
      );
    }
    if (partialAllowed) {
      return (
        <>
          <col width="3%" />
          <col width="17%" />
          <col width="10%" />
          <col width="10%" />
          <col width="10%" />
          <col width="10%" />
          <col width="10%" />
          <col width="10%" />
          <col width="20%" />
        </>
      );
    }
    if (hasDiscounts) {
      return (
        <>
          <col width="4%" />
          <col width="19%" />
          <col width="11%" />
          <col width="11%" />
          <col width="11%" />
          <col width="11%" />
          <col width="13%" />
          <col width="20%" />
        </>
      );
    }
    return (
      <>
        <col width="4%" />
        <col width="19%" />
        <col width="15%" />
        <col width="15%" />
        <col width="14%" />
        <col width="14%" />
        <col width="20%" />
      </>
    );
  };

  return (
    <div className={platform === 'mobile' || (settingsMenuVisible && matches) ? classesMobile.tableWrapContainer : ''}>
      <TableContainer
        className={platform === 'mobile' || (settingsMenuVisible && matches) ? classesMobile.tableWrap : classesDesktop.tableWrap}
        data-cy="payments-due-list"
      >
        <InfiniteScroll
          pageStart={0}
          loadMore={loadPage}
          hasMore={hasMoreData}
          loader={
            <div
              className={
                platform === 'mobile' || (settingsMenuVisible && matches)
                  ? classesMobile.loadingSpinner
                  : classesDesktop.loadingSpinner
              }
            >
              <CircularProgress key={0} size={42} />
            </div>
          }
          initialLoad={false}
          useWindow={false}
        >
          <Table
            className={platform === 'mobile' || (settingsMenuVisible && matches) ? classesMobile.table : classesDesktop.table}
            aria-label="Payments Due List"
            data-cy="payments-due-table"
            stickyHeader
          >
            {!(platform === 'mobile' || (settingsMenuVisible && matches)) && getColumns()}
            {!(platform === 'mobile' || (settingsMenuVisible && matches)) && (
              <TableHeader
                cells={partialAllowed ? partialHeaderCells : desktopHeaderCells}
                order={paymentsDueOrder}
                orderBy={paymentsDueOrderBy}
                sortData={sortPaymentsDueData}
                isPaymentsDue={true}
              />
            )}
            <TableBody>
              {/* Related Mobile view */}
              {(platform === 'mobile' || (settingsMenuVisible && matches)) && (
                <>
                  {paymentsDueError && (
                    <TableRow>
                      <TableCell variant="footer" size="small" className={classesMobile.errorMessage}>
                        {errorMessage}
                      </TableCell>
                    </TableRow>
                  )}
                  {!paymentsDueError && !paymentsDue && <>{skeletonMobile}</>}
                  {!paymentsDueError && paymentsDue?.length === 0 && (
                    <TableRow>
                      <TableCell variant="footer" size="small" className={classesMobile.emptyMessage}>
                        {emptyMessage}
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}

              {/* Related Desktop view */}
              {!(platform === 'mobile' || (settingsMenuVisible && matches)) && (
                <>
                  {paymentsDueError && (
                    <TableRow>
                      <TableCell variant="footer" size="small" colSpan={6} className={classesDesktop.errorMessage}>
                        {errorMessage}
                      </TableCell>
                    </TableRow>
                  )}
                  {!paymentsDueError && !paymentsDue && <>{skeletonDesktop}</>}
                  {!paymentsDueError && paymentsDue?.length === 0 && (
                    <TableRow>
                      <TableCell variant="footer" size="small" colSpan={6} className={classesDesktop.emptyMessage}>
                        {emptyMessage}
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
              {!paymentsDueError &&
                paymentsDueData?.map((paymentsDueRow, index) => {
                  if (!(hideUnselected && !paymentsDueRow.isSelected)) {
                    return (
                      <PaymentsDueRow
                        key={index}
                        data={paymentsDueRow}
                        rowIndex={index}
                        isMobileSize={platform === 'mobile' || (settingsMenuVisible && matches)}
                        consolidatedAllowed={!!selectedMerchantInfo?.features?.paymentRequests.consolidatedPayment}
                        partialAllowed={!!selectedMerchantInfo?.features?.paymentRequests.partialPayment}
                        creditAllowed={!!selectedMerchantInfo?.features?.creditMemos.enabled}
                        settingsMenuVisible={settingsMenuVisible}
                        onClickPay={onClickPay}
                        onRowClicked={(rowIndex: number) => {
                          handleRowClicked(rowIndex);
                        }}
                        showPaymentDetails={showPaymentDetails}
                        handleRowAmountChange={handleAmountChange}
                        handleAmountError={handleAmountError}
                        amountError={amountError}
                        discountEndVisible={hasDiscounts}
                      />
                    );
                  }
                  return <></>;
                })}
            </TableBody>
          </Table>
        </InfiniteScroll>
      </TableContainer>
      <div className={platform === 'mobile' || (settingsMenuVisible && matches) ? classesMobile.tableFooter : ''}>
        <Divider />
        <PaymentsDueFooter
          isMobileView={platform === 'mobile' || (settingsMenuVisible && matches)}
          currency={paymentsDue ? paymentsDue[0]?.currency : CurrencyType.Usd}
          consolidatedAllowed={!!selectedMerchantInfo?.features?.paymentRequests.consolidatedPayment}
          partialAllowed={!!selectedMerchantInfo?.features?.paymentRequests.partialPayment}
          totalAmount={getTotalAmountDue()}
          totalInvoices={paymentsDueConnection?.totalCount || 0}
          selectedInvoices={selectedInvoices}
          selectedInvoiceAmount={selectedInvoicesAmount}
          toggleHideUnselected={() => {
            setHideUnselected(!hideUnselected);
          }}
          handleMakePayment={handlePayment}
          amountError={amountError}
          totalPartialAmount={getTotalPartialAmount()}
          hideUnselected={hideUnselected}
        />
      </div>
    </div>
  );
};

export default forwardRef(PaymentsDueList);
